import React from 'react';
import { InitialData } from '../../../infrastructure/form-viewmodel-controller';
import { AbstractFormViewModelController } from '../../../infrastructure/implementations/form-viewmodel-controller';
import { action, observable } from 'mobx';
import { configList, EventHubFormField } from './event-hub-form.config';
import { TObject } from '../../../infrastructure/type-extensions';
import { FormFieldProps } from '../../_events/_manage-event/_connections/_meeting-connections-form/send-connections-email-form/send-connections-email-form.viewmodel';
import { alertServerError, showNotification } from '../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../constants/common-labels.constants';
import * as IoC from 'typescript-ioc';
import { Container, Inject } from 'typescript-ioc';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { EventHubsDAO } from '../../../api/implementations/event-hubs.dao';
import { IEventHubDTO, IEventHubRequestDTO, IEventHubsDAO } from '../../../api/event-hubs.dao';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { EVENT_HUB_TRACKING_EVENTS } from '../../../tracking-service/tracking-values';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { IAlertsService } from '../../../infrastructure/alerts';
import { FormattedMessage } from 'react-intl';
import { FORM_ARE_YOU_SURE_TO_TEXT } from '../../_blueprints/conections-templates/connections-template-form/connection-template-form.texts';
import { Space } from '../../../constants/html-character-entities';
import { DELETE_HUB_TEXT, DELETE_HUB_UNDONE_TEXT, DELETE_HUB_WITH_NAME_TEXT, HUB_DELETED_NOTIFICATION_TEXT } from '../event-hubs.text';


export class EventHubFormVM {
    public static createFormVM(initialData: InitialData = {}): AbstractFormViewModelController {
        return new class extends AbstractFormViewModelController {
            constructor() {
                super(initialData, 'EventHubFormController', configList);
            }
        }();
    }

    private eventHubDetails: IEventHubDTO | undefined;
    private formDataCtrl: AbstractFormViewModelController;

    @observable
    private loading: boolean = false;

    constructor(
        private readonly accountData: { accountId: number; accountName: string },
        private readonly saveCompletedCallback: () => Promise<void>,
        private readonly deleteCompletedCallback: () => Promise<void>,
        private readonly eventHubId?: number,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
        @Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    public get isEditMode(): boolean {
        return !!this.eventHubId;
    }

    public get hasDeletePermission(): boolean {
        return hasPermission(Permission.DELETE_EVENT_HUB);
    }

    @action
    public async initialize(): Promise<void> {
        if (this.eventHubId) {
            await this.loadEventHub(this.eventHubId);
        }
        this.formDataCtrl = EventHubFormVM.createFormVM(this.composeFormCtrlInitialData());
    }

    public getFormFields = (): TObject<FormFieldProps> => {
        const formFields = {};
        for (const key of Object.values(EventHubFormField)) {
            formFields[key] = this.formDataCtrl.getFormVmValuePropsByKey(key);
        }
        return formFields;
    };

    public submitForm = (): Promise<boolean> => {
        if (!this.formDataCtrl.isVmValid()) {
            this.formDataCtrl.setVmTouched();
            return Promise.resolve(false);
        } else {
            return this.callSubmitAction();
        }
    };

    private callSubmitAction(): Promise<boolean> {
        return this.isEditMode ? this.editEventHub() : this.createEventHub();
    }

    @action.bound
    public async createEventHub(): Promise<boolean> {
        this.startLoading();
        try {
            const dto = this.composeEventHubRequestDTO();
            await this.eventHubsDAO.createEventHub(this.accountId, dto);
            this.saveCompletedCallback();
            this.trackMixPanelEvent(EVENT_HUB_TRACKING_EVENTS.CREATE_EVENT_HUB);
            return true;
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
            return false;
        } finally {
            this.stopLoading();
        }
    }

    @action.bound
    public async editEventHub(): Promise<boolean> {
        this.startLoading();
        try {
            const dto = this.composeEventHubRequestDTO();
            await this.eventHubsDAO.updateEventHub(this.accountId, this.eventHubId, dto);
            this.saveCompletedCallback();
            this.trackMixPanelEvent(EVENT_HUB_TRACKING_EVENTS.EDIT_EVENT_HUB);
            return true;
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
            return false;
        } finally {
            this.stopLoading();
        }
    }

    public showDeleteHubConfirmation = (): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage {...FORM_ARE_YOU_SURE_TO_TEXT} />
                    <Space />
                    <span className='alert-message--danger word-break'>
                        <FormattedMessage {...DELETE_HUB_WITH_NAME_TEXT} values={{ hubName: this.eventHubDetails.eventHubName }} />
                    </span>
                    <Space />
                    <FormattedMessage {...DELETE_HUB_UNDONE_TEXT} />
                </span>
            ),
            confirmButtonProps: {
                text: <FormattedMessage {...DELETE_HUB_TEXT} />,
                modifier: 'confirm-warn',
                handler: this.deleteEventHub,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @action.bound
    public async deleteEventHub(): Promise<void> {
        try {
            await this.eventHubsDAO.deleteEventHub(this.accountId, this.eventHubId);
            this.deleteCompletedCallback();
            showNotification(
                <FormattedMessage
                    {...HUB_DELETED_NOTIFICATION_TEXT}
                    values={{ hubName: this.eventHubDetails.eventHubName }}
                />
            );
            this.trackMixPanelEvent(EVENT_HUB_TRACKING_EVENTS.DELETE_EVENT_HUB);
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
        }
    }

    @action.bound
    public async loadEventHub(eventHubId: number): Promise<void> {
        this.startLoading();
        try {
            this.eventHubDetails = await this.eventHubsDAO.getEventHub(this.accountId, eventHubId);
        } catch (e) {
            const errorMessage = this.errorParsingService.extractMessage(e);
            alertServerError(errorText(), errorMessage);
        } finally {
            this.stopLoading();
        }
    }

    private composeFormCtrlInitialData(): InitialData {
        if (this.eventHubDetails) {
            return {
                [EventHubFormField.EVENT_HUB_NAME]: this.eventHubDetails.eventHubName,
            };
        } else {
            return {};
        }
    }

    private composeEventHubRequestDTO(): IEventHubRequestDTO {
        return {
            eventHubName: (this.formDataCtrl.getValue(EventHubFormField.EVENT_HUB_NAME) as string).trim(),
        };
    }

    private get accountId(): number {
        return this.accountData.accountId;
    }

    private get accountName(): string {
        return this.accountData.accountName;
    }

    private trackMixPanelEvent = (eventName: EVENT_HUB_TRACKING_EVENTS): void => {
        this.trackingService.get().track(eventName, this.composeTrackingPropsByEvent(eventName));
    };

    private composeTrackingPropsByEvent(event: EVENT_HUB_TRACKING_EVENTS): TObject<string | number> {
        switch (event) {
            case EVENT_HUB_TRACKING_EVENTS.CREATE_EVENT_HUB:
                return this.getCommonTrackingProps();
            case EVENT_HUB_TRACKING_EVENTS.EDIT_EVENT_HUB:
            case EVENT_HUB_TRACKING_EVENTS.DELETE_EVENT_HUB:
                return {
                    ...this.getCommonTrackingProps(),
                    'Hub ID': this.eventHubId
                };
            default:
                return {};
        }
    }

    private getCommonTrackingProps(): TObject<string | number> {
        return {
            'Account Name': this.accountName,
            'Account ID': this.accountId,
        };
    }

    @action
    private startLoading(): void {
        this.loading = true;
    }
    @action
    private stopLoading(): void {
        this.loading = false;
    }
}